/**
 * Application Configuration
 *
 * Configures the Angular application with all necessary providers for standalone components.
 * This includes routing, HTTP client with interceptors, animations, and PrimeNG configuration.
 *
 * @module AppConfig
 */

import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import localeEn from '@angular/common/locales/en';

import { routes } from './app.routes';
import { MessageService } from 'primeng/api';
import {
  authInterceptor,
  errorInterceptor,
  loadingInterceptor,
  csrfInterceptor,
  securityHeadersInterceptor,
  languageInterceptor
} from './core/interceptors';

registerLocaleData(localeAr);
registerLocaleData(localeEn);

/**
 * Application configuration object
 *
 * Provides all necessary services and configurations for the standalone Angular application:
 * - Zone change detection with event coalescing for better performance
 * - Router with component input binding for route parameters
 * - HTTP client with interceptors (to be added in Phase 2)
 * - Browser animations for PrimeNG components and transitions
 *
 * @example
 * ```typescript
 * // Used in main.ts
 * bootstrapApplication(AppComponent, appConfig);
 * ```
 */
export const appConfig: ApplicationConfig = {
  providers: [
    // Zone change detection with performance optimization
    provideZoneChangeDetection({ eventCoalescing: true }),

    // Router configuration with component input binding
    // This allows route parameters to be bound directly to component inputs
    provideRouter(routes, withComponentInputBinding()),

    // HTTP client configuration with interceptors
    // Interceptors are executed in the order they are provided
    provideHttpClient(
      withInterceptors([
        securityHeadersInterceptor,  // Adds security headers to requests
        languageInterceptor,         // Adds Accept-Language header
        authInterceptor,             // Adds Bearer token to requests
        csrfInterceptor,             // Adds CSRF token to state-changing requests
        errorInterceptor,            // Handles error responses
        loadingInterceptor           // Manages loading state
      ])
    ),

    // Animations provider for PrimeNG components and custom transitions
    provideAnimations(),

    // PrimeNG MessageService for toast notifications (used by ErrorInterceptor)
    MessageService
  ]
};
